/* ── WORK_TRACK — Projects ────────────────────────────────── */

import {
  $,
  esc,
  icon,
  api,
  state,
  toast,
  registerView,
  openModal,
  closeModal,
  setFieldError,
  clearFieldErrors,
  fmtMin,
  emptyHtml
} from './core';
import { loadDashboard } from './dashboard';
import type { Project } from './types';

export function renderProjects(): void {
  const grid = $('#projectsGrid');
  if (!state.projects.length) {
    grid.innerHTML = `<div class="panel">${emptyHtml(icon('folder', 30), 'No projects yet', 'Projects organize your tickets and time — create your first one.', { label: '+ New project', on: 'projects' })}</div>`;
    return;
  }
  grid.innerHTML = state.projects
    .map(
      p => `
    <div class="project-card">
      <div class="pc-head">
        <span class="pc-name">${esc(p.name)}</span>
      </div>
      <p class="pc-desc">${esc(p.description || 'No description.')}</p>
      <div class="pc-meta">
        <div class="pc-stat"><strong>${fmtMin(p.total_minutes || 0)}</strong> logged</div>
        <div class="pc-stat"><strong>${p.task_count || 0}</strong> tickets</div>
      </div>
      <div class="pc-actions">
        <button data-paction="edit" data-project="${p.id}">Edit</button>
        <button data-paction="delete" data-project="${p.id}">Delete</button>
      </div>
    </div>`
    )
    .join('');
}

document.addEventListener('click', e => {
  const target = e.target as HTMLElement;
  const projEdit = target.closest('[data-paction="edit"]');
  if (projEdit) {
    openProjectModal(Number(projEdit.getAttribute('data-project')));
    return;
  }
  const projDel = target.closest('[data-paction="delete"]');
  if (projDel) {
    deleteProject(Number(projDel.getAttribute('data-project')));
    return;
  }
});

$('#newProjectBtn').addEventListener('click', () => openProjectModal(null));

function openProjectModal(id: number | null): void {
  const p = id ? state.projects.find(x => x.id === id) : null;
  const card = openModal(`
    <form class="modal-form" id="projectForm">
      <div class="modal-head"><h2>${p ? 'Edit project' : 'New project'}</h2><button type="button" class="modal-close">${icon('x', 16)}</button></div>
      <div class="field"><label>Name</label><input name="name" required maxlength="120" value="${esc(p ? p.name : '')}"></div>
      <div class="field"><label>Description</label><textarea name="description" maxlength="2000">${esc(p ? p.description || '' : '')}</textarea></div>
      <div class="modal-actions">
        <button type="button" class="btn btn-ghost" id="modalCancel">Cancel</button>
        <button type="submit" class="btn btn-primary">${p ? 'Save' : 'Create'}</button>
      </div>
    </form>`);
  card.querySelector('#modalCancel')?.addEventListener('click', closeModal);
  let submitting = false;
  const projectFormEl = card.querySelector<HTMLFormElement>('#projectForm')!;
  projectFormEl.addEventListener('submit', async e => {
    e.preventDefault();
    if (submitting) return;
    clearFieldErrors(card);
    const f = new FormData(projectFormEl);
    const name = String(f.get('name') || '').trim();
    if (!name) {
      const inp = card.querySelector<HTMLInputElement>('input[name="name"]')!;
      setFieldError(inp, 'Name is required');
      inp.focus();
      return;
    }
    if (name.length > 120) {
      setFieldError(card.querySelector<HTMLInputElement>('input[name="name"]')!, 'Maximum 120 characters');
      return;
    }
    submitting = true;
    const submitBtn = card.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('loading');
      submitBtn.innerHTML = `<span class="spinner"></span>${esc(p ? 'Saving…' : 'Creating…')}`;
    }
    const payload = {
      name,
      description: String(f.get('description') || '')
    };
    try {
      if (p) {
        const d = await api<Project>(`/api/projects/${p.id}`, 'PUT', payload);
        const idx = state.projects.findIndex(x => x.id === p.id);
        if (idx >= 0) state.projects[idx] = d;
        closeModal();
        renderProjects();
        if (state.view === 'dashboard') loadDashboard();
        toast('Project updated');
      } else {
        const d = await api<Project>('/api/projects', 'POST', payload);
        state.projects.push(d);
        renderProjects();
        if (state.view === 'dashboard') loadDashboard();
        closeModal();
        toast('Project created');
      }
    } catch (err) {
      toast((err as Error).message, true);
    } finally {
      submitting = false;
      const sb = card.querySelector<HTMLButtonElement>('button[type="submit"]');
      if (sb) {
        sb.disabled = false;
        sb.classList.remove('loading');
        sb.innerHTML = esc(p ? 'Save' : 'Create');
      }
    }
  });
}

async function deleteProject(id: number): Promise<void> {
  if (!confirm('Delete this project? Its tickets, comments, history and time entries will be removed.')) return;
  try {
    await api(`/api/projects/${id}`, 'DELETE');
    state.projects = state.projects.filter(p => p.id !== id);
    state.tasks = state.tasks.filter(t => t.project_id !== id);
    renderProjects();
    toast('Project deleted');
  } catch (e) {
    toast((e as Error).message, true);
  }
}

registerView('projects', renderProjects);
